import type { ComponentProps } from 'react';
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import type { Nullable, StringMap } from 'corgi-types';
import type { ColorProps, FontSizeProps, FontWeightProps, LineHeightProps } from 'styled-system';
import { color, fontSize, fontWeight, lineHeight, space } from 'styled-system';

import { Flex } from 'common/components/Flex';
import SeparatorWithText from 'common/components/SeparatorWithText';
import { Text } from 'common/components/Text';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import ConfigurationCard from './ConfigurationCard';
import { trackDrawerView } from './tracking';
import type { AlternativeArticleVariation } from './types';

interface ConfigurationTrapProps {
  alternativeVariations: AlternativeArticleVariation[];
  title: string;
  setRedirectURL: (url: string) => void;
  selectedValues: StringMap<string>;
  listLength: number;
}

const UnstyledContainer = styled(Flex)``;

const Container = (props: ComponentProps<typeof UnstyledContainer>) => (
  <UnstyledContainer flexDirection="column" {...props} />
);

const UnstyledDescription = styled(Text)``;

const Description = (props: ComponentProps<typeof UnstyledDescription>) => (
  <UnstyledDescription
    color="sys.neutral.text.default"
    fontSize="fs_16"
    fontWeight={700}
    lineHeight="lh_24"
    mb="sp_24"
    {...props}
  />
);

const UnstyledSeparator = styled(SeparatorWithText)<
  SpaceProps & FontSizeProps & LineHeightProps & ColorProps & FontWeightProps
>`
  ${color};
  ${fontSize};
  ${fontWeight}
  ${lineHeight};
  ${space};
`;

const Separator = (props: ComponentProps<typeof UnstyledSeparator>) => (
  <UnstyledSeparator
    color="sys.neutral.text.default"
    fontSize={['fs_14', null, null, 'fs_16']}
    fontWeight={700}
    lineHeight={['lh_20', null, null, 'lh_24']}
    my="sp_24"
    {...props}
  />
);

const ConfigurationTrap = ({
  alternativeVariations,
  selectedValues,
  title: optionTitle,
  setRedirectURL,
  listLength,
}: ConfigurationTrapProps) => {
  const { r, t } = useLocalization();
  const [selectedURL, setSelectedURL] = useState<Nullable<string>>(null);

  useEffect(() => {
    if (selectedURL) {
      setRedirectURL(selectedURL);
    }
  }, [selectedURL]);

  useEffect(() => {
    if (listLength) {
      trackDrawerView(listLength);
    }
  }, [listLength]);

  return (
    <Container>
      <Description>
        {r.t('h24_configuration_trap_choose_alternative', {
          title: optionTitle,
        })}
      </Description>
      {alternativeVariations.map((item, index) => {
        return (
          <div key={item.key} data-testid={`alternative-card-${item.key}`}>
            {item.groups && (
              <ConfigurationCard
                {...item}
                index={index}
                onSelect={setSelectedURL}
                groupTitle={selectedValues[item.key]}
                optionTitle={optionTitle}
                selectedURL={selectedURL}
                listLength={listLength}
              />
            )}
            {index !== alternativeVariations.length - 1 && <Separator>{t('h24_or')}</Separator>}
          </div>
        );
      })}
    </Container>
  );
};

export default ConfigurationTrap;
